import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Confetti from 'react-confetti';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowDownTrayIcon,
  ArrowRightIcon,
  ArrowUpTrayIcon,
  CalendarDaysIcon,
  ClipboardDocumentListIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import PageHeader from '../ui/PageHeader';
import Button from '../ui/Button';
import TableToolbar from '../ui/TableToolbar';
import EmptyState from '../ui/EmptyState';
import Select from '../ui/Select';
import { useConfirmDialog } from '../ui/ConfirmDialog';
import ActionOutcomePanel from '../ui/ActionOutcomePanel';
import ViewState from '../ui/ViewState';
import { ListPageSkeleton } from '../ui/PageSkeleton';
import TruncatedText from '../ui/TruncatedText';
import CreateProspectWizard from './CreateProspectWizard';
import ProspectEditModal from './ProspectEditModal';
import ProspectNotesCalendarModal from './ProspectNotesCalendarModal';
import ProspectLevelStars from '../ui/ProspectLevelStars';
import CopyablePhone from '../ui/CopyablePhone';
import { useProspectConfig } from '../../hooks/useProspectConfig';
import { platformRequestConfig } from '../../utils/platform/platformRequest';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  prospectStatusLabel,
  prospectStatusTextClass,
  resolveProspectStatuses,
  isPartnerProspect,
} from '../../utils/prospectStatus';

const CONFETTI_HOLD_MS = 3200;
const CONFETTI_EXIT_EASE = [0.23, 1, 0.32, 1];

function prospectSortValue(row, columnKey) {
  switch (columnKey) {
    case 'prospection_level':
      return row.prospection_level || 0;
    case 'name':
      return row.name || '';
    case 'address': {
      const parts = [row.address || '', row.apartment || ''].filter(Boolean);
      return parts.join(' ');
    }
    case 'phone':
      return row.phone || '';
    case 'email':
      return row.email || '';
    case 'status':
      return row.status || '';
    case 'type':
      return isPartnerProspect(row) ? 'partner' : 'client';
    case 'vendor':
      return row.vendor_username || '';
    default:
      return '';
  }
}

function SortableHeader({ label, columnKey, sort, onToggle, align = 'left' }) {
  const active = sort.key === columnKey;
  const arrow = active ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : '';
  const alignClass = align === 'center' ? 'text-center' : 'text-left';
  return (
    <th
      className={`p-3 ${alignClass} text-xs font-semibold uppercase tracking-wide text-secondary-500 `}
    >
      <button
        type="button"
        onClick={() => onToggle(columnKey)}
        className={`inline-flex items-center gap-1 hover:text-orange-600  ${
          align === 'center' ? 'w-full justify-center' : ''
        }`}
      >
        {label}
        {arrow}
      </button>
    </th>
  );
}

function ProspectStatusCell({ value, prospectId, onUpdate, t, statuses, className = '', centered = false }) {
  return (
    <Select
      value={value}
      variant="compact"
      centered={centered}
      onChange={(e) => onUpdate(prospectId, { status: e.target.value })}
      labelClassName={prospectStatusTextClass(value, statuses)}
      triggerClassName={`cursor-pointer border-0 bg-transparent px-2 py-1.5 text-xs font-semibold shadow-none ring-0 backdrop-blur-none focus:outline-none focus:ring-2 focus:ring-orange-500/40  ${className}`}
      panelClassName="min-w-[11rem]"
      className={centered ? 'mx-auto w-auto max-w-[11rem]' : 'w-full max-w-full sm:max-w-[11rem]'}
      aria-label={prospectStatusLabel(value, t, statuses)}
    >
      {statuses.map((status) => (
        <option key={status.code} value={status.code}>
          {prospectStatusLabel(status.code, t, statuses)}
        </option>
      ))}
    </Select>
  );
}

function VendorProspectsView({ mode = 'vendor', companyFilter = null, requestCompanyId = null }) {
  const isAdmin = mode === 'admin';
  const { t, formatMessage } = useLanguage();
  const { confirm, dialog } = useConfirmDialog();
  const [searchParams, setSearchParams] = useSearchParams();
  const { prospectStatuses, partnerStatuses, defaultStatus, partnerDefaultStatus } =
    useProspectConfig(requestCompanyId);
  const companyRequest = platformRequestConfig(requestCompanyId);
  const p = t.vendor?.prospects || {};
  const fileInputRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);

  useEffect(() => {
    if (isAdmin || searchParams.get('create') !== '1') return;
    setWizardOpen(true);
    setSearchParams({}, { replace: true });
  }, [isAdmin, searchParams, setSearchParams]);
  const [submitting, setSubmitting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [enrollingId, setEnrollingId] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [actionOutcome, setActionOutcome] = useState(null);
  const [confettiActive, setConfettiActive] = useState(false);
  const confettiDismissTimerRef = useRef(null);
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });
  const [editProspect, setEditProspect] = useState(null);
  const [calendarProspect, setCalendarProspect] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [vendorList, setVendorList] = useState([]);

  const prospectsApiBase = isAdmin ? '/api/admin/prospects' : '/api/vendor/prospects';

  const applyProspectUpdate = useCallback((updated) => {
    setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    setEditProspect((prev) => (prev?.id === updated.id ? updated : prev));
    setCalendarProspect((prev) => (prev?.id === updated.id ? updated : prev));
  }, []);

  const fetchProspects = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const config = { withCredentials: true };
      if (requestCompanyId) {
        config.params = { company_id: requestCompanyId };
      }
      const response = await axios.get(prospectsApiBase, config);
      setRows(response.data || []);
    } catch (error) {
      console.error('Error loading prospects:', error);
      const message = getApiErrorMessage(error, t, formatMessage, p.failedToLoad);
      setLoadError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [formatMessage, p.failedToLoad, prospectsApiBase, requestCompanyId, t]);

  useEffect(() => {
    fetchProspects();
  }, [fetchProspects]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    let cancelled = false;
    const config = { withCredentials: true };
    if (requestCompanyId) config.params = { company_id: requestCompanyId };
    axios.get('/api/admin/vendors', config)
      .then((res) => {
        if (!cancelled) setVendorList(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isAdmin, requestCompanyId]);

  useEffect(() => {
    const onResize = () =>
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    return () => {
      if (confettiDismissTimerRef.current) clearTimeout(confettiDismissTimerRef.current);
    };
  }, []);

  const fireEnrollConfetti = useCallback(() => {
    if (confettiDismissTimerRef.current) clearTimeout(confettiDismissTimerRef.current);

    setConfettiActive(true);

    confettiDismissTimerRef.current = setTimeout(() => {
      setConfettiActive(false);
    }, CONFETTI_HOLD_MS);
  }, []);

  const toggleSort = (columnKey) => {
    setSort((prev) =>
      prev.key === columnKey
        ? { key: columnKey, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key: columnKey, dir: 'asc' }
    );
  };

  const sorted = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filtered = rows.filter((row) => {
      // When the API was already scoped with requestCompanyId, skip redundant
      // client filtering — and never drop rows that omit company_id.
      if (
        companyFilter
        && !requestCompanyId
        && row.company_id != null
        && String(row.company_id) !== String(companyFilter)
      ) {
        return false;
      }
      if (statusFilter !== 'all' && row.status !== statusFilter) return false;
      if (typeFilter !== 'all') {
        const rowType = isPartnerProspect(row) ? 'partner' : 'client';
        if (rowType !== typeFilter) return false;
      }
      if (isAdmin && vendorFilter !== 'all' && row.vendor_username !== vendorFilter) return false;
      if (!normalizedSearch) return true;
      const haystack = [
        row.name,
        row.email,
        row.phone,
        row.address,
        row.apartment,
        row.vendor_username,
        row.company_name,
        row.status,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(normalizedSearch);
    });

    const copy = [...filtered];
    copy.sort((a, b) => {
      const av = String(prospectSortValue(a, sort.key)).toLowerCase();
      const bv = String(prospectSortValue(b, sort.key)).toLowerCase();
      if (av < bv) return sort.dir === 'asc' ? -1 : 1;
      if (av > bv) return sort.dir === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [rows, sort, searchTerm, statusFilter, vendorFilter, typeFilter, companyFilter, requestCompanyId, isAdmin]);

  const vendorOptions = useMemo(() => {
    const vendors = [...new Set(rows.map((r) => r.vendor_username).filter(Boolean))].sort();
    return vendors;
  }, [rows]);

  const showCompanyColumn = isAdmin && rows.some((row) => row.company_name);

  const hasActiveFilters =
    searchTerm.trim() !== '' || statusFilter !== 'all' || vendorFilter !== 'all' || typeFilter !== 'all';

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setVendorFilter('all');
    setTypeFilter('all');
  };

  const tb = t.common.table;

  async function handleCreate(form) {
    setSubmitting(true);
    const partner = form.prospect_type === 'partner';
    try {
      await axios.post(
        '/api/vendor/prospects',
        {
          name: form.name.trim(),
          email: form.email || undefined,
          phone: form.phone || undefined,
          address: form.address || undefined,
          apartment: form.apartment || undefined,
          notes: form.notes || undefined,
          prospect_type: form.prospect_type || 'client',
          ...(!partner && form.is_employed !== null ? { is_employed: form.is_employed } : {}),
          ...(!partner && form.is_married !== null ? { is_married: form.is_married } : {}),
          ...(!partner && form.housing_type ? { housing_type: form.housing_type } : {}),
          ...(!partner ? { prospection_level: form.prospection_level || undefined } : {}),
        },
        { withCredentials: true }
      );
      toast.success(p.createdSuccess);
      setWizardOpen(false);
      await fetchProspects();
    } catch (error) {
      toast.error(getApiErrorMessage(error, t, formatMessage, p.failedToCreate));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleUpdate(prospectId, patch) {
    try {
      const response = await axios.put(
        `${prospectsApiBase}/${prospectId}`,
        patch,
        companyRequest
      );
      applyProspectUpdate(response.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, t, formatMessage, p.failedToUpdate));
      await fetchProspects();
    }
  }

  async function handleEnroll(row) {
    const partner = isPartnerProspect(row);
    const confirmed = await confirm({
      title: formatMessage(p.confirmEnrollTitle || 'Enroll {name}?', { name: row.name }),
      message: partner
        ? (p.confirmEnrollPartnerMessage || p.confirmEnrollMessage)
        : p.confirmEnrollMessage,
      confirmLabel: partner ? (p.enrollPartner || p.enroll) : p.enroll,
      confirmVariant: 'primary',
    });
    if (!confirmed) return;

    setEnrollingId(row.id);
    try {
      const endpoint = isAdmin
        ? `/api/admin/prospects/${row.id}/enroll`
        : `/api/vendor/prospects/${row.id}/enroll`;
      await axios.post(endpoint, {}, companyRequest);
      fireEnrollConfetti();
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      setEditProspect((prev) => (prev?.id === row.id ? null : prev));
      setCalendarProspect((prev) => (prev?.id === row.id ? null : prev));
      toast.success(
        partner
          ? formatMessage(p.partnerEnrolledSuccess || p.enrolledSuccess, { name: row.name })
          : formatMessage(p.enrolledSuccess, { name: row.name })
      );
      const outcomeCopy = partner
        ? (t.ux?.actionOutcome?.partnerEnrolled || t.ux?.actionOutcome?.prospectEnrolled)
        : t.ux?.actionOutcome?.prospectEnrolled;
      setActionOutcome({
        id: `enroll-${row.id}-${Date.now()}`,
        title: outcomeCopy?.title || formatMessage(p.enrolledSuccess, { name: row.name }),
        description: row.name,
        nextStep: outcomeCopy?.nextStep,
      });
    } catch (error) {
      toast.error(getApiErrorMessage(error, t, formatMessage, p.failedToEnroll));
    } finally {
      setEnrollingId(null);
    }
  }

  async function handleAddNote(prospectId, text, noteDate) {
    try {
      const response = await axios.put(
        `${prospectsApiBase}/${prospectId}`,
        {
          add_note: text,
          ...(noteDate ? { note_date: noteDate } : {}),
        },
        companyRequest
      );
      applyProspectUpdate(response.data);
      toast.success(p.noteAddedSuccess);
    } catch (error) {
      toast.error(getApiErrorMessage(error, t, formatMessage, p.failedToUpdate));
      throw error;
    }
  }

  async function handleAddAppointment(prospectId, { appointmentDate, startTime, endTime, note }) {
    try {
      const response = await axios.put(
        `${prospectsApiBase}/${prospectId}`,
        {
          add_appointment: true,
          appointment_date: appointmentDate,
          start_time: startTime,
          end_time: endTime,
          ...(note ? { appointment_note: note } : {}),
        },
        companyRequest
      );
      applyProspectUpdate(response.data);
      toast.success(p.appointmentScheduledSuccess);
    } catch (error) {
      toast.error(getApiErrorMessage(error, t, formatMessage, p.failedToScheduleAppointment));
      throw error;
    }
  }

  async function handleSaveProspect(prospectId, data) {
    const partner = data.prospect_type === 'partner';
    try {
      const response = await axios.put(
        `${prospectsApiBase}/${prospectId}`,
        {
          name: data.name,
          email: data.email || undefined,
          phone: data.phone || undefined,
          address: data.address || undefined,
          apartment: data.apartment || undefined,
          status: data.status,
          prospect_type: data.prospect_type,
          ...(partner
            ? { is_employed: null, is_married: null, housing_type: null }
            : {
                is_employed: data.is_employed ?? null,
                is_married: data.is_married ?? null,
                housing_type: data.housing_type || null,
                prospection_level: data.prospection_level || undefined,
              }),
        },
        companyRequest
      );
      applyProspectUpdate(response.data);
      toast.success(p.updatedSuccess);
    } catch (error) {
      toast.error(getApiErrorMessage(error, t, formatMessage, p.failedToUpdate));
      throw error;
    }
  }

  async function handleDelete(prospectId, name) {
    if (!(await confirm({
      message: formatMessage(p.confirmDelete, { name }),
      title: t.common.confirmDestructiveTitle,
      confirmLabel: t.common.delete,
    }))) return;
    try {
      await axios.delete(`/api/vendor/prospects/${prospectId}`, { withCredentials: true });
      toast.success(p.deletedSuccess);
      setRows((prev) => prev.filter((r) => r.id !== prospectId));
    } catch (error) {
      toast.error(getApiErrorMessage(error, t, formatMessage, p.failedToDelete));
    }
  }

  async function handleDownloadTemplate() {
    setDownloadingTemplate(true);
    try {
      const response = await axios.get('/api/vendor/prospects/import/template', {
        responseType: 'blob',
        withCredentials: true,
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'prospects_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error(getApiErrorMessage(error, t, formatMessage, p.failedToDownloadTemplate));
    } finally {
      setDownloadingTemplate(false);
    }
  }

  async function handleImport(file) {
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post('/api/vendor/prospects/import-excel', formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const count = response.data?.imported ?? 0;
      toast.success(formatMessage(p.importSuccess, { count }));
      await fetchProspects();
    } catch (error) {
      toast.error(getApiErrorMessage(error, t, formatMessage, p.failedToImport));
    } finally {
      setImporting(false);
    }
  }

  return (
    <>
      <AnimatePresence>
        {confettiActive ? (
          <motion.div
            key="enroll-confetti"
            className="pointer-events-none fixed inset-0 z-[200]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.4, ease: CONFETTI_EXIT_EASE } }}
            exit={{ opacity: 0, transition: { duration: 2.6, ease: CONFETTI_EXIT_EASE } }}
          >
            <Confetti
              width={windowSize.width}
              height={windowSize.height}
              recycle={false}
              numberOfPieces={320}
              gravity={0.2}
              tweenDuration={7500}
            />
          </motion.div>
        ) : null}
      </AnimatePresence>

    <div className="min-w-0 space-y-6">
      {dialog}
      <PageHeader
        eyebrow={isAdmin ? t.common.admin : t.vendor.dashboard}
        icon={ClipboardDocumentListIcon}
        title={p.title}
        subtitle={isAdmin ? p.adminSubtitle : p.subtitle}
        action={
          isAdmin ? null : (
          <div className="grid w-full min-w-0 max-w-full grid-cols-1 gap-2 sm:grid-cols-2 lg:flex lg:flex-wrap lg:justify-end lg:gap-2 [&_button]:w-full [&_button]:justify-center lg:[&_button]:w-auto">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) void handleImport(file);
              }}
            />
            <Button
              variant="secondary"
              size="small"
              icon={ArrowDownTrayIcon}
              loading={downloadingTemplate}
              onClick={() => void handleDownloadTemplate()}
            >
              {p.downloadTemplate}
            </Button>
            <Button
              variant="secondary"
              size="small"
              icon={ArrowUpTrayIcon}
              loading={importing}
              onClick={() => fileInputRef.current?.click()}
            >
              {p.importExcel}
            </Button>
            <Button
              size="small"
              icon={PlusIcon}
              className="sm:col-span-2 lg:col-span-1"
              onClick={() => setWizardOpen(true)}
            >
              {p.newProspect}
            </Button>
          </div>
          )
        }
      />

      <ActionOutcomePanel outcome={actionOutcome} onDismiss={() => setActionOutcome(null)} />

      {loadError ? <ViewState error={loadError} onRetry={fetchProspects} /> : null}

      {!loadError ? (
      <>
      <p className="break-words text-sm text-secondary-600 ">
        {p.excelHint}
      </p>

      {!isAdmin ? (
      <CreateProspectWizard
        isOpen={wizardOpen}
        onClose={() => setWizardOpen(false)}
        isSubmitting={submitting}
        onSubmit={handleCreate}
      />
      ) : null}

      <ProspectEditModal
        prospect={editProspect}
        isOpen={Boolean(editProspect)}
        onClose={() => setEditProspect(null)}
        onSave={handleSaveProspect}
        onAddNote={handleAddNote}
        prospectStatuses={prospectStatuses}
        partnerStatuses={partnerStatuses}
        defaultStatus={defaultStatus}
        partnerDefaultStatus={partnerDefaultStatus}
      />

      <ProspectNotesCalendarModal
        prospect={calendarProspect}
        isOpen={Boolean(calendarProspect)}
        onClose={() => setCalendarProspect(null)}
        onAddNote={handleAddNote}
        onAddAppointment={handleAddAppointment}
      />

      <div className="card-premium min-w-0 overflow-hidden hover:scale-100">
        {!loading && rows.length > 0 ? (
          <div className="border-b border-secondary-200/60 p-3  sm:p-4">
            <TableToolbar
              searchValue={searchTerm}
              onSearchChange={setSearchTerm}
              searchPlaceholder={p.searchPlaceholder}
              filters={[
                {
                  id: 'type',
                  label: p.filterByType || 'Type',
                  value: typeFilter,
                  onChange: setTypeFilter,
                  options: [
                    { value: 'all', label: tb.filterAll },
                    { value: 'client', label: p.typeClient || 'Client' },
                    { value: 'partner', label: p.typePartner || 'Partner' },
                  ],
                },
                {
                  id: 'status',
                  label: p.filterByStatus || tb.filterByStatus,
                  value: statusFilter,
                  onChange: setStatusFilter,
                  options: [
                    { value: 'all', label: tb.filterAll },
                    ...[...prospectStatuses, ...partnerStatuses]
                      .filter((s, i, arr) => arr.findIndex((x) => x.code === s.code) === i)
                      .map((status) => ({
                        value: status.code,
                        label: prospectStatusLabel(status.code, t, [...prospectStatuses, ...partnerStatuses]),
                      })),
                  ],
                },
                ...(isAdmin
                  ? [
                      {
                        id: 'vendor',
                        label: p.filterByVendor || tb.filterByVendor,
                        value: vendorFilter,
                        onChange: setVendorFilter,
                        options: [
                          { value: 'all', label: tb.filterAll },
                          ...vendorOptions.map((vendor) => ({
                            value: vendor,
                            label: vendor,
                          })),
                        ],
                      },
                    ]
                  : []),
              ]}
              hasActiveFilters={hasActiveFilters}
              onClearFilters={clearFilters}
              resultCount={sorted.length}
              totalCount={rows.length}
            />
          </div>
        ) : null}

        {loading ? (
          <ListPageSkeleton rows={4} showHeader={false} />
        ) : rows.length === 0 ? (
          <p className="p-6 text-sm text-secondary-600 ">
            {isAdmin
              ? p.adminEmpty
              : p.empty ||
                'No prospects yet. Import Excel or use New prospect to add your first lead.'}
          </p>
        ) : sorted.length === 0 ? (
          <EmptyState
            compact
            title={p.noMatchingProspects || tb.noMatchingRows}
            description={tb.clearFilters}
            className="mx-4 my-6"
          />
        ) : (
          <>
          <div className="hidden overflow-x-auto lg:block">
          <table className="w-full min-w-[800px] text-left text-sm">
            <thead className="border-b border-secondary-200/60 ">
              <tr>
                <SortableHeader
                  label={p.colProspectionLevel}
                  columnKey="prospection_level"
                  sort={sort}
                  onToggle={toggleSort}
                />
                <SortableHeader
                  label={p.colName}
                  columnKey="name"
                  sort={sort}
                  onToggle={toggleSort}
                />
                <SortableHeader
                  label={p.colType}
                  columnKey="type"
                  sort={sort}
                  onToggle={toggleSort}
                  align="center"
                />
                {isAdmin ? (
                  <SortableHeader
                    label={p.colVendor}
                    columnKey="vendor"
                    sort={sort}
                    onToggle={toggleSort}
                  />
                ) : null}
                {showCompanyColumn ? (
                  <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide text-secondary-500 ">
                    {t.platformAdmin?.company || 'Company'}
                  </th>
                ) : null}
                <SortableHeader
                  label={p.colAddress}
                  columnKey="address"
                  sort={sort}
                  onToggle={toggleSort}
                />
                <SortableHeader
                  label={p.colPhone}
                  columnKey="phone"
                  sort={sort}
                  onToggle={toggleSort}
                />
                <SortableHeader
                  label={p.colStatus}
                  columnKey="status"
                  sort={sort}
                  onToggle={toggleSort}
                  align="center"
                />
                <th className="p-3 text-center text-xs font-semibold uppercase tracking-wide text-secondary-500 ">
                  {p.colActions}
                </th>
                <th className="p-3 text-center text-xs font-semibold uppercase tracking-wide text-secondary-500 ">
                  {p.colEnroll || p.enroll}
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-secondary-100 last:border-0 "
                >
                  <td className="p-3 text-center">
                    <ProspectLevelStars
                      value={row.prospection_level || 0}
                      readOnly
                      size="sm"
                      centered
                      ariaLabel={`${p.prospectionLevelLabel} — ${row.name}`}
                    />
                  </td>
                  <td className="p-3 max-w-[14rem]">
                    <div className="flex items-center gap-2">
                      <TruncatedText className="font-medium text-secondary-900 ">
                        {row.name}
                      </TruncatedText>
                    </div>
                    {row.email ? (
                      <TruncatedText className="text-xs text-secondary-500 ">
                        {row.email}
                      </TruncatedText>
                    ) : null}
                  </td>
                  <td className="p-3 text-center">
                    <span
                      className={
                        isPartnerProspect(row)
                          ? 'text-xs font-semibold text-violet-700 '
                          : 'text-xs font-semibold text-orange-700 '
                      }
                    >
                      {isPartnerProspect(row) ? p.typePartner || 'Socio' : p.typeClient || 'Cliente'}
                    </span>
                  </td>
                  {isAdmin ? (
                    <td className="p-3 text-secondary-600  max-w-[12rem]">
                      {isPartnerProspect(row) ? (
                        <Select
                          variant="compact"
                          value={row.vendor_id ? String(row.vendor_id) : ''}
                          onChange={(e) => void handleUpdate(row.id, { vendor_id: e.target.value || null })}
                          aria-label={p.assignLeader || 'Assign leader'}
                        >
                          <option value="">{p.noLeader || 'Sin líder'}</option>
                          {vendorList.map((vendor) => (
                            <option key={vendor.id} value={vendor.id}>
                              {vendor.username}
                            </option>
                          ))}
                        </Select>
                      ) : (
                        <TruncatedText>{row.vendor_username || '—'}</TruncatedText>
                      )}
                    </td>
                  ) : null}
                  {showCompanyColumn ? (
                    <td className="p-3 text-secondary-600  max-w-[10rem]">
                      <TruncatedText>{row.company_name || '—'}</TruncatedText>
                    </td>
                  ) : null}
                  <td className="p-3 text-secondary-600  max-w-[16rem]">
                    <TruncatedText>
                      {[row.address, row.apartment].filter(Boolean).join(', ') || '—'}
                    </TruncatedText>
                  </td>
                  <td className="p-3 text-secondary-600  whitespace-nowrap">
                    <CopyablePhone value={row.phone} />
                  </td>
                  <td className="p-3 text-center">
                    <ProspectStatusCell
                      value={row.status}
                      prospectId={row.id}
                      onUpdate={handleUpdate}
                      statuses={resolveProspectStatuses(row.prospect_type, prospectStatuses, partnerStatuses)}
                      t={t}
                      centered
                    />
                  </td>
                  <td className="p-3 text-center">
                    <div className="mobile-scroll-x inline-flex max-w-[9rem] items-center justify-center gap-1 sm:max-w-none sm:overflow-visible sm:pb-0">
                      <button
                        type="button"
                        onClick={() => setCalendarProspect(row)}
                        className="touch-target inline-flex items-center justify-center rounded-xl text-secondary-400 transition-colors hover:bg-cyan-50 hover:text-cyan-600  "
                        aria-label={p.notesCalendarTitle}
                        title={p.notesCalendarTitle}
                      >
                        <CalendarDaysIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditProspect(row)}
                        className="touch-target inline-flex items-center justify-center rounded-xl text-secondary-400 transition-colors hover:bg-orange-50 hover:text-orange-600  "
                        aria-label={p.editProspect}
                        title={p.editProspect}
                      >
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                      {!isAdmin ? (
                      <button
                        type="button"
                        onClick={() => handleDelete(row.id, row.name)}
                        className="touch-target inline-flex items-center justify-center rounded-xl text-secondary-400 transition-colors hover:bg-error-50 hover:text-error-600  "
                        aria-label={p.delete}
                        title={p.delete}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                      ) : null}
                    </div>
                  </td>
                  <td className="p-3 text-center">
                    <Button
                      size="small"
                      variant={isPartnerProspect(row) ? 'partner' : 'primary'}
                      icon={ArrowRightIcon}
                      loading={enrollingId === row.id}
                      disabled={enrollingId === row.id}
                      onClick={() => void handleEnroll(row)}
                      aria-label={isPartnerProspect(row) ? (p.enrollPartner || p.enroll) : p.enroll}
                      title={isPartnerProspect(row) ? (p.enrollPartner || p.enroll) : p.enroll}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          <div className="divide-y divide-secondary-100  lg:hidden">
            {sorted.map((row) => (
              <div key={row.id} className="space-y-3 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-secondary-900 ">{row.name}</div>
                    </div>
                    {row.email ? (
                      <TruncatedText className="text-xs text-secondary-500 ">{row.email}</TruncatedText>
                    ) : null}
                    {isAdmin && isPartnerProspect(row) ? (
                      <div className="mt-2">
                        <Select
                          variant="compact"
                          value={row.vendor_id ? String(row.vendor_id) : ''}
                          onChange={(e) => void handleUpdate(row.id, { vendor_id: e.target.value || null })}
                          aria-label={p.assignLeader || 'Assign leader'}
                        >
                          <option value="">{p.noLeader || 'Sin líder'}</option>
                          {vendorList.map((vendor) => (
                            <option key={vendor.id} value={vendor.id}>
                              {vendor.username}
                            </option>
                          ))}
                        </Select>
                      </div>
                    ) : null}
                    {isAdmin && !isPartnerProspect(row) && row.vendor_username ? (
                      <div className="mt-1 text-xs text-secondary-500 ">
                        {row.vendor_username}
                      </div>
                    ) : null}
                  </div>
                  <ProspectLevelStars
                    value={row.prospection_level || 0}
                    readOnly
                    size="sm"
                    ariaLabel={`${p.prospectionLevelLabel} — ${row.name}`}
                  />
                </div>

                <div className="grid grid-cols-1 gap-2 text-sm">
                  <div className="min-w-0">
                    <span className="text-xs font-medium uppercase tracking-wide text-secondary-500 ">
                      {p.colType}
                    </span>
                    <p className="mt-0.5 text-secondary-700 ">
                      {isPartnerProspect(row) ? p.typePartner || 'Socio' : p.typeClient || 'Cliente'}
                    </p>
                  </div>
                  <div className="min-w-0">
                    <span className="text-xs font-medium uppercase tracking-wide text-secondary-500 ">
                      {p.colAddress}
                    </span>
                    <p className="mt-0.5 break-words text-secondary-700 ">
                      {[row.address, row.apartment].filter(Boolean).join(', ') || '—'}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs font-medium uppercase tracking-wide text-secondary-500 ">
                      {p.colPhone}
                    </span>
                    <CopyablePhone
                      value={row.phone}
                      className="mt-0.5 text-secondary-700 "
                    />
                  </div>
                </div>

                <div className="text-center">
                  <span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-secondary-500 ">
                    {p.colStatus}
                  </span>
                  <ProspectStatusCell
                    value={row.status}
                    prospectId={row.id}
                    onUpdate={handleUpdate}
                    statuses={resolveProspectStatuses(row.prospect_type, prospectStatuses, partnerStatuses)}
                    t={t}
                    centered
                  />
                </div>

                <div className="flex flex-wrap items-center gap-2 border-t border-secondary-100 pt-3 ">
                  <button
                    type="button"
                    onClick={() => setCalendarProspect(row)}
                    className="touch-target inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-medium text-secondary-600 transition-colors hover:bg-cyan-50 hover:text-cyan-600   "
                  >
                    <CalendarDaysIcon className="h-4 w-4" />
                    {p.notesCalendarTitle}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditProspect(row)}
                    className="touch-target inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-medium text-secondary-600 transition-colors hover:bg-orange-50 hover:text-orange-600   "
                  >
                    <PencilSquareIcon className="h-4 w-4" />
                    {p.editProspect}
                  </button>
                  {!isAdmin ? (
                    <button
                      type="button"
                      onClick={() => handleDelete(row.id, row.name)}
                      className="touch-target inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-medium text-secondary-600 transition-colors hover:bg-error-50 hover:text-error-600   "
                    >
                      <TrashIcon className="h-4 w-4" />
                      {p.delete}
                    </button>
                  ) : null}
                  <Button
                    size="small"
                    variant={isPartnerProspect(row) ? 'partner' : 'primary'}
                    icon={ArrowRightIcon}
                    loading={enrollingId === row.id}
                    disabled={enrollingId === row.id}
                    onClick={() => void handleEnroll(row)}
                    className="ml-auto"
                  >
                    {isPartnerProspect(row) ? (p.enrollPartner || p.enroll) : p.enroll}
                  </Button>
                </div>
              </div>
            ))}
          </div>
          </>
        )}
      </div>
      </>
      ) : null}
    </div>
    </>
  );
}

export default VendorProspectsView;
